/**
 * How a daily challenge time is written down.
 *
 * ONE implementation, used by the live timer, the tile badges, the completion
 * overlay, the calendar and the history table — because a family comparing
 * times across five screens must never have to work out whether "1:05" and
 * "65.0s" are the same number.
 *
 * G2 (Jake, 2026-08-05): **no milliseconds and no tenths, anywhere a time is
 * rendered.** A tenth of a second is not a thing anybody in this family is
 * racing over, and a digit that changes ten times a second next to a puzzle is
 * noise. STORAGE and RANKING are untouched — `minigame_plays.time_ms` is still
 * milliseconds and the leaderboard still orders by it, so two times that
 * display the same can still be ordered correctly. This function is the ONE
 * place the two representations meet, which is why the guard for G2 is a test
 * of this file plus a source sweep that nothing else formats a duration.
 *
 * Seconds are FLOORED, not rounded: a clock that has been running for 0.9s has
 * not reached one second, and a stopwatch that shows 1 before a second has
 * elapsed is a stopwatch nobody trusts.
 */

import type { DailyChallengeScore } from "./types";

/** `0:04`, `1:07`, `12:00` — minutes and whole seconds. Never a fraction. */
export function formatTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
  const totalSeconds = Math.floor(Math.max(0, ms) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** `Aug 4` / `Aug 4, 2025` — the history table's date column. */
export function formatPuzzleDate(iso: string, today = new Date()): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  const label = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return y === today.getFullYear() ? label : `${label}, ${y}`;
}

/** `Tuesday, August 4` — the calendar and the day view's heading. */
export function formatLongDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/** "3 hints" / "1 hint" / "" — the suffix under a time. */
export function formatHints(hints: number): string {
  if (!hints) return "";
  return hints === 1 ? "1 hint" : `${hints} hints`;
}

/**
 * § R2-2 — HOW A PLAY'S SCORE IS WRITTEN DOWN, whichever number the game is
 * scored by. Jake: *"Pinpoint should report on the number of guesses and hints,
 * not time"*.
 *
 * ONE function, for the same reason there is one `formatTime`: the tile badge,
 * the completion overlay's headline, the overlay's day list and the history row
 * all answer "how did that play go?" and a family reading four screens must not
 * find four answers. The metric comes off `DailyChallengeListing.score`, which the
 * server sets from the ONE declaration in `src/daily-challenges/registry.ts` — nothing
 * here knows the word "pinpoint".
 *
 * A DURATION IS STILL FORMATTED IN EXACTLY ONE PLACE (G2). The `"time"` branch
 * delegates to `formatTime` rather than reimplementing it, so this file gains a
 * caller and not a second representation.
 *
 * THE SINGULAR MATTERS. Winning Pinpoint on the first clue is "1 guess", and a
 * screen that says "1 guesses" is a screen nobody wrote carefully.
 *
 *   formatScore("time",    { time_ms: 67_400, … })  → "1:07"
 *   formatScore("guesses", { guesses: 3, … })       → "3 guesses"
 *   formatScore("guesses", { guesses: 1, … })       → "1 guess"
 *   formatScore("guesses", { guesses: null, … })    → "—"   (nothing recorded)
 */
export function formatScore(
  metric: DailyChallengeScore,
  row: { time_ms: number | null | undefined; guesses?: number | null }
): string {
  if (metric !== "guesses") return formatTime(row.time_ms);
  const guesses = row.guesses;
  if (guesses === null || guesses === undefined || !Number.isFinite(guesses)) return "—";
  return guesses === 1 ? "1 guess" : `${guesses} guesses`;
}

/**
 * § F1 (2026-08-06) — WAS IT A LOSS?
 *
 * `minigame_plays.completed` is a boolean and a lost Pinpoint is a COMPLETED
 * play (five guesses spent, clock stopped, `solved:false` in the result) — so
 * every surface that reports a score is also reporting losses, and until this
 * existed all of them reported one as a win. `rank.ts` already sorted wins above
 * losses and migration 023 already returned the column; what was missing was
 * that anything RENDERED it.
 *
 * `null`/`undefined` reads as a WIN, which is what a completed play of a game
 * that has no losing state is — and it is the only value the seven time games
 * ever carry.
 */
export function isLostPlay(
  metric: DailyChallengeScore,
  row: { solved?: boolean | null }
): boolean {
  return metric === "guesses" && row.solved === false;
}

/**
 * § F1 — a play's score WITH its outcome, for every list that reports one.
 *
 * `formatScore` answers "what number?"; this answers "how did it go?", and the
 * two are different questions the moment a game can be lost. "5 guesses" on a
 * tile badge, a history row and the overlay's day list is the SAME string
 * whether it was a win in five or a loss having burned all five, and a family
 * comparing those rows cannot tell them apart.
 *
 * It DELEGATES rather than reformatting, so the number is still written down in
 * exactly one place (G2) and the loss marker is written down in exactly one
 * place here.
 *
 * THE MARKER LEADS. These are all `truncate`d boxes on a 320px phone, and a
 * suffix is the first thing an ellipsis eats — "Lost — 5 gue…" still says the
 * one thing that matters, "5 guesses — l…" does not.
 *
 *   formatResult("guesses", { guesses: 5, solved: false, … }) → "Lost — 5 guesses"
 *   formatResult("guesses", { guesses: 5, solved: true,  … }) → "5 guesses"
 *   formatResult("time",    { time_ms: 67_400, … })           → "1:07"
 */
export function formatResult(
  metric: DailyChallengeScore,
  row: { time_ms: number | null | undefined; guesses?: number | null; solved?: boolean | null },
  { compact = false }: { compact?: boolean } = {}
): string {
  const score = formatScore(metric, row);
  if (!isLostPlay(metric, row)) {
    // ── § F74 (2026-08-08) — AND THE COUNT LOSES ITS NOUN, ON THE ONE SURFACE
    //    THAT LISTS EVERYBODY ────────────────────────────────────────────────
    //
    // Jake: *"on the daily challenge main menu, you need to show the other player's
    // times with my time."* Five players is five scores on one 272px line, and
    // `"3 guesses"` is 44px of which 32px is the word — five of them is 439px
    // of strip where five `"1:30"`s are 308px. The word is also the ONE token
    // on that line the reader already has: the strip only exists once I have
    // completed the game (§ F50), so my own badge is always beside it saying
    // `5 GUESSES` in full, and every number under it is a guess count by
    // construction.
    //
    // The NUMBER is untouched — § F8's rule is that a score is never cut inside
    // its own digits, and `3` is the whole of the digits. Every other surface
    // (`compact: false` — the overlay, the history, the game screen) still
    // writes the noun, because none of them is listing six people across a
    // phone.
    return compact && metric === "guesses" && score !== "—" ? String(row.guesses) : score;
  }
  // § G1 (2026-08-06) — WHERE THE ROOM RUNS OUT, THE OUTCOME OUTRANKS THE COUNT.
  //
  // `Lost — 5 guesses` is about three times the width of `1:07`, and on the
  // tile's one-line family list it starved the NAME cell until the leader read
  // `#1 EL…` at 390px and `#…` at 320px — a tile saying somebody lost without
  // saying who. The name is the most important token on that line, so the SCORE
  // is what gives way, and it gives way by shortening rather than by being cut:
  // `Lost` is the whole of what a five-guess loss means on a 129px tile, and
  // the exact count is one tap away on the game screen, the overlay and the
  // history, all of which pass `compact: false` and are unchanged.
  return compact ? "Lost" : `Lost — ${score}`;
}

/**
 * § F1 — the completion overlay's headline sentence.
 *
 * A lost play used to render `"Pinpoint solved"` over a trophy and twenty-four
 * pieces of confetti — visually identical to a five-guess WIN ranked above it.
 * A celebration is a claim about what happened, and this is the one place that
 * claim is written.
 */
export function completionTitle(gameName: string, solved: boolean | null | undefined): string {
  return solved === false ? `${gameName} — out of guesses` : `${gameName} solved`;
}
